import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
    Loader2, Save, Trash2, Plus, Receipt, AlertTriangle, Ban, FolderOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { settingsApi } from '../../lib/api';

const fmtEur = (v) => v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const feeColor = (p) => (p <= 10 ? 'bg-emerald-400' : p <= 50 ? 'bg-amber-400' : 'bg-rose-500');

/**
 * Timeline-Visualisierung der Staffelung inkl. Live-Pin & Warnungen.
 * Kapselt die Berechnung aller Segmente und Marker.
 */
function CancellationTimeline({ tiers, previewDays }) {
    const timeline = useMemo(() => {
        const clean = (tiers || []).map((t) => ({
            days_before: parseInt(t.days_before, 10) || 0,
            fee_percent: Math.max(0, Math.min(100, parseInt(t.fee_percent, 10) || 0)),
            label: (t.label || '').trim(),
        }));
        const sortedDesc = [...clean].sort((a, b) => b.days_before - a.days_before);
        const previewInt = parseInt(previewDays, 10);
        const pd = Number.isFinite(previewInt) ? previewInt : 0;

        const maxTierDay = sortedDesc.length ? sortedDesc[0].days_before : 14;
        const viewMax = Math.max(maxTierDay + 7, pd + 3, 14);
        const minView = Math.min(0, pd) - (pd < 0 ? 2 : 0);
        const span = Math.max(viewMax - minView, 1);

        const segs = [];
        let prev = viewMax;
        sortedDesc.forEach((t) => {
            segs.push({ from: prev, to: t.days_before, fee: t.fee_percent, label: t.label });
            prev = t.days_before;
        });
        if (prev > 0 && sortedDesc.length) {
            const last = sortedDesc[sortedDesc.length - 1];
            segs.push({
                from: prev, to: 0, fee: last.fee_percent,
                label: last.label ? `(Fallback) ${last.label}` : '(Fallback)',
                fallback: true,
            });
        }
        if (minView < 0) {
            segs.push({ from: 0, to: minView, fee: 100, label: 'Nach Seminarbeginn', afterStart: true });
        }

        const dayCount = {};
        clean.forEach((t) => { dayCount[t.days_before] = (dayCount[t.days_before] || 0) + 1; });
        const duplicates = Object.keys(dayCount).filter((k) => dayCount[k] > 1).map(Number);

        let monotone = true;
        for (let i = 1; i < sortedDesc.length; i++) {
            if (sortedDesc[i].fee_percent < sortedDesc[i - 1].fee_percent) { monotone = false; break; }
        }
        return { segs, viewMax, minView, span, sortedDesc, duplicates, monotone, pd };
    }, [tiers, previewDays]);

    const dayToPct = (day) => Math.max(0, Math.min(100, ((timeline.viewMax - day) / timeline.span) * 100));

    return (
        <div className="bg-white rounded-md border p-4" data-testid="cancel-timeline">
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-medium text-slate-600">Staffelungs-Zeitleiste</div>
                <div className="flex items-center gap-3 text-[11px] text-slate-500">
                    <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-emerald-400" /> 0–10 %</span>
                    <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-amber-400" /> 11–50 %</span>
                    <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-rose-500" /> 51–100 %</span>
                </div>
            </div>

            <div className="relative pt-6 pb-7 px-1">
                <div className="relative h-3 rounded bg-slate-100 overflow-hidden">
                    {timeline.segs.map((seg, idx) => {
                        const left = dayToPct(seg.from);
                        const right = dayToPct(seg.to);
                        const width = Math.max(0, right - left);
                        if (width <= 0) return null;
                        return (
                            <div
                                key={idx}
                                className={`absolute top-0 h-full ${feeColor(seg.fee)} ${seg.afterStart ? 'opacity-70 [background-image:repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(255,255,255,0.45)_4px,rgba(255,255,255,0.45)_8px)]' : ''}`}
                                style={{ left: `${left}%`, width: `${width}%` }}
                                title={`${seg.label || 'Stufe'} · ${seg.fee}%`}
                            />
                        );
                    })}
                </div>

                {timeline.sortedDesc.map((t, idx) => {
                    const pct = dayToPct(t.days_before);
                    return (
                        <div key={`mk-${idx}`} className="absolute top-0" style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}>
                            <div className="text-[10px] text-slate-600 whitespace-nowrap font-medium">
                                {t.days_before} T · {t.fee_percent}%
                            </div>
                            <div className="mx-auto h-3 w-px bg-slate-400 mt-1" />
                        </div>
                    );
                })}
                <div className="absolute top-0" style={{ left: `${dayToPct(0)}%`, transform: 'translateX(-50%)' }}>
                    <div className="text-[10px] font-semibold text-slate-700">Beginn</div>
                    <div className="mx-auto h-3 w-px bg-slate-700 mt-1" />
                </div>

                <div
                    className="absolute"
                    style={{ left: `${dayToPct(timeline.pd)}%`, top: '34px', transform: 'translateX(-50%)' }}
                    data-testid="cancel-timeline-pin"
                >
                    <div className="h-3 w-px bg-teal-600 mx-auto" />
                    <div className="mt-0.5 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold bg-teal-600 text-white whitespace-nowrap">
                        {timeline.pd} T
                    </div>
                </div>
            </div>

            {(timeline.duplicates.length > 0 || !timeline.monotone) && (
                <div className="mt-3 space-y-1 text-[11px] text-amber-700" data-testid="cancel-timeline-warnings">
                    {timeline.duplicates.length > 0 && (
                        <div className="flex items-start gap-1">
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                            Doppelte Stufe(n) bei Tag {timeline.duplicates.join(', ')} – bitte eine davon entfernen.
                        </div>
                    )}
                    {!timeline.monotone && (
                        <div className="flex items-start gap-1">
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                            Die Gebühr sollte mit sinkender Vorlaufzeit ansteigen. Aktuell fällt sie zwischen zwei Stufen.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Beispielrechnung mit Formularfeldern (Preis, Tage, Zahlungsstatus) und Ergebniskarten.
 * Spiegelt 1:1 die Backend-Logik aus routes/cancellations.py::_calc_quote.
 */
function CancellationPreview({ tiers, afterStartBlocked }) {
    const [previewPrice, setPreviewPrice] = useState(390);
    const [previewDays, setPreviewDays] = useState(10);
    const [previewPaid, setPreviewPaid] = useState(true);

    const quote = useMemo(() => {
        const price = Math.max(0, parseFloat(previewPrice) || 0);
        const days = parseInt(previewDays, 10);
        const cleanTiers = (tiers || []).map((t) => ({
            days_before: parseInt(t.days_before, 10) || 0,
            fee_percent: Math.max(0, Math.min(100, parseInt(t.fee_percent, 10) || 0)),
            label: (t.label || '').trim(),
        }));
        const isAfterStart = Number.isFinite(days) && days < 0;
        const canSelfCancel = !(isAfterStart && afterStartBlocked);

        let feePercent = 100;
        let tierLabel = '';
        if (!Number.isFinite(days)) {
            feePercent = 100;
            tierLabel = 'Datum unbekannt';
        } else {
            const sorted = [...cleanTiers].sort((a, b) => b.days_before - a.days_before);
            const hit = sorted.find((t) => days > t.days_before);
            if (hit) {
                feePercent = hit.fee_percent;
                tierLabel = hit.label;
            } else if (sorted.length) {
                feePercent = sorted[sorted.length - 1].fee_percent;
                tierLabel = sorted[sorted.length - 1].label;
            }
        }

        const feeAmount = Math.round(price * feePercent) / 100;
        const paidAmount = previewPaid ? price : 0;
        const refund = Math.max(0, Math.round((paidAmount - feeAmount) * 100) / 100);
        const open = previewPaid ? 0 : feeAmount;
        return { feePercent, feeAmount, refund, open, tierLabel, canSelfCancel };
    }, [tiers, afterStartBlocked, previewPrice, previewDays, previewPaid]);

    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="cancel-preview">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Receipt className="h-4 w-4" />
                Beispielrechnung (live)
            </div>
            <p className="text-xs text-slate-500">
                Prüfen Sie Ihre Staffelung, bevor Sie speichern. Änderungen an den Stufen oben werden
                sofort übernommen.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                    <Label className="text-xs">Seminarpreis (€)</Label>
                    <Input
                        type="number" min="0" step="0.01"
                        value={previewPrice}
                        onChange={(e) => setPreviewPrice(e.target.value)}
                        data-testid="cancel-preview-price"
                    />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">Tage bis Seminarbeginn</Label>
                    <Input
                        type="number"
                        value={previewDays}
                        onChange={(e) => setPreviewDays(e.target.value)}
                        data-testid="cancel-preview-days"
                    />
                    <p className="text-[11px] text-slate-400">Negative Zahl = nach Seminarbeginn</p>
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">Zahlungsstatus</Label>
                    <label className="flex items-center gap-2 h-9 px-2 rounded-md border bg-white cursor-pointer">
                        <Switch
                            checked={previewPaid}
                            onCheckedChange={setPreviewPaid}
                            data-testid="cancel-preview-paid"
                        />
                        <span className="text-sm">{previewPaid ? 'bereits bezahlt' : 'noch nicht bezahlt'}</span>
                    </label>
                </div>
            </div>

            <CancellationTimeline tiers={tiers} previewDays={previewDays} />

            {!quote.canSelfCancel ? (
                <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                    <AlertTriangle className="h-4 w-4 mt-0.5" />
                    <div>
                        Das Seminar hat bereits begonnen – Selbst-Stornierung durch Mitglieder ist laut
                        aktuellen Regeln <strong>nicht</strong> erlaubt. Admins können weiterhin manuell stornieren.
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="bg-white rounded-md border p-3">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Stufe</div>
                        <div className="font-medium text-slate-800 truncate" title={quote.tierLabel}>
                            {quote.tierLabel || '—'}
                        </div>
                    </div>
                    <div className="bg-white rounded-md border p-3">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Gebühr</div>
                        <div className="font-medium text-slate-800" data-testid="cancel-preview-fee">
                            {quote.feePercent}% · {fmtEur(quote.feeAmount)}
                        </div>
                    </div>
                    <div className="bg-white rounded-md border p-3">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Rückerstattung</div>
                        <div className="font-medium text-emerald-700" data-testid="cancel-preview-refund">
                            {fmtEur(quote.refund)}
                        </div>
                    </div>
                    <div className="bg-white rounded-md border p-3">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Offene Forderung</div>
                        <div className="font-medium text-slate-800">{fmtEur(quote.open)}</div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function CancellationRulesCard() {
    const [tiers, setTiers] = useState([]);
    const [afterStartBlocked, setAfterStartBlocked] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        settingsApi.getCancellationRules()
            .then((r) => {
                setTiers(r.data.tiers || []);
                setAfterStartBlocked(!!r.data.after_start_blocked);
            })
            .catch(() => { /* Default bleibt */ })
            .finally(() => setLoading(false));
    }, []);

    const updateTier = (idx, field, value) => {
        const next = [...tiers];
        next[idx] = { ...next[idx], [field]: value };
        setTiers(next);
    };
    const addTier = () => setTiers([...tiers, { days_before: 0, fee_percent: 100, label: '' }]);
    const removeTier = (idx) => setTiers(tiers.filter((_, i) => i !== idx));

    const handleSave = async () => {
        setSaving(true);
        try {
            const cleaned = tiers.map((t) => ({
                days_before: parseInt(t.days_before, 10) || 0,
                fee_percent: Math.max(0, Math.min(100, parseInt(t.fee_percent, 10) || 0)),
                label: (t.label || '').trim(),
            }));
            await settingsApi.saveCancellationRules({ tiers: cleaned, after_start_blocked: afterStartBlocked });
            toast.success('Storno-Regeln gespeichert');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally { setSaving(false); }
    };

    return (
        <Card data-testid="cancellation-rules-card">
            <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                    <Ban className="h-5 w-5" />
                    Storno-Regeln
                </CardTitle>
                <CardDescription>
                    Staffelung der Stornogebühr in Abhängigkeit zum Tagesabstand vom Seminarbeginn.
                    Wird bei jeder Storno-Anfrage des Mitglieds live im Portal gerechnet.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="space-y-2">
                    {tiers.map((t, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`cancel-tier-${idx}`}>
                            <div className="col-span-3 space-y-1">
                                <Label className="text-xs">Mehr als (Tage)</Label>
                                <Input type="number" min="0" value={t.days_before}
                                    onChange={(e) => updateTier(idx, 'days_before', e.target.value)}
                                    disabled={loading}
                                    data-testid={`cancel-tier-days-${idx}`}
                                />
                            </div>
                            <div className="col-span-3 space-y-1">
                                <Label className="text-xs">Gebühr (%)</Label>
                                <Input type="number" min="0" max="100" value={t.fee_percent}
                                    onChange={(e) => updateTier(idx, 'fee_percent', e.target.value)}
                                    disabled={loading}
                                    data-testid={`cancel-tier-percent-${idx}`}
                                />
                            </div>
                            <div className="col-span-5 space-y-1">
                                <Label className="text-xs">Beschreibung (für Mitglied)</Label>
                                <Input value={t.label || ''}
                                    onChange={(e) => updateTier(idx, 'label', e.target.value)}
                                    placeholder="z. B. 7–14 Tage vor Beginn"
                                    disabled={loading}
                                />
                            </div>
                            <div className="col-span-1">
                                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeTier(idx)} title="Stufe entfernen">
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addTier} disabled={loading} data-testid="cancel-add-tier">
                        <Plus className="h-4 w-4 mr-1" /> Stufe hinzufügen
                    </Button>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                        checked={afterStartBlocked}
                        onCheckedChange={setAfterStartBlocked}
                        disabled={loading}
                        data-testid="cancel-after-start-switch"
                    />
                    <span className="text-sm">Nach Seminarbeginn keine Selbst-Stornierung erlauben</span>
                </label>

                <CancellationPreview tiers={tiers} afterStartBlocked={afterStartBlocked} />

                <div className="flex justify-end pt-2 border-t">
                    <Button onClick={handleSave} disabled={saving || loading} data-testid="save-cancel-rules">
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Speichern
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

export function CancellationReasonsCard() {
    const [reasons, setReasons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        settingsApi.getCancellationReasons()
            .then((r) => setReasons(r.data.reasons || []))
            .catch(() => { /* Default */ })
            .finally(() => setLoading(false));
    }, []);

    const updateReason = (idx, field, value) => {
        const next = [...reasons];
        next[idx] = { ...next[idx], [field]: value };
        setReasons(next);
    };
    const addReason = () => setReasons([...reasons, { key: '', label: '', sort_order: reasons.length + 1 }]);
    const removeReason = (idx) => setReasons(reasons.filter((_, i) => i !== idx));

    const handleSave = async () => {
        setSaving(true);
        try {
            await settingsApi.saveCancellationReasons({
                reasons: reasons
                    .filter((r) => (r.label || '').trim())
                    .map((r, i) => ({
                        key: (r.key || '').trim().toLowerCase().replace(/\s+/g, '_') || `r${i}`,
                        label: r.label.trim(),
                        sort_order: parseInt(r.sort_order, 10) || (i + 1),
                    })),
            });
            toast.success('Storno-Gründe gespeichert');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally { setSaving(false); }
    };

    return (
        <Card data-testid="cancellation-reasons-card">
            <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                    <FolderOpen className="h-5 w-5" />
                    Storno-Gründe
                </CardTitle>
                <CardDescription>
                    Auswahl, die Mitglieder beim Beantragen einer Stornierung sehen.
                    Optional können sie zusätzlich einen Freitext angeben.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                {reasons.map((r, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`cancel-reason-${idx}`}>
                        <div className="col-span-3 space-y-1">
                            <Label className="text-xs">Schlüssel</Label>
                            <Input value={r.key || ''} onChange={(e) => updateReason(idx, 'key', e.target.value)} placeholder="krankheit" disabled={loading} />
                        </div>
                        <div className="col-span-7 space-y-1">
                            <Label className="text-xs">Anzeige-Label</Label>
                            <Input value={r.label || ''} onChange={(e) => updateReason(idx, 'label', e.target.value)} placeholder="Krankheit" disabled={loading} />
                        </div>
                        <div className="col-span-1 space-y-1">
                            <Label className="text-xs">Sort.</Label>
                            <Input type="number" value={r.sort_order || 1} onChange={(e) => updateReason(idx, 'sort_order', e.target.value)} disabled={loading} />
                        </div>
                        <div className="col-span-1">
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeReason(idx)} title="Eintrag entfernen">
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                ))}
                <div className="flex items-center justify-between border-t pt-3">
                    <Button variant="outline" size="sm" onClick={addReason} disabled={loading} data-testid="cancel-reason-add">
                        <Plus className="h-4 w-4 mr-1" /> Grund hinzufügen
                    </Button>
                    <Button onClick={handleSave} disabled={saving || loading} data-testid="save-cancel-reasons">
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Speichern
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
